import React, { useEffect, useState, useMemo } from "react";
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TextField } from "@mui/material";
import dayjs from "dayjs";
import {
  ThinDropdownWithSearch,
  ThinTextField,
} from "../common/GeneralComponent";
import { fetchData } from "../../utils/functions";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";

//import LocationSelector from "../common/LocationSelector";

const InformationPanel = ({ DqaApproved, DqaRejected }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [houseHoldInfo, setHouseHoldInfo] = useState(null);
  const [respondentName, setRespondentName] = useState("");
  const [respondentLine, setRespondentLine] = useState("");
  const [hhName, setHhName] = useState("");
  const [hhCnic, setHhCnic] = useState("");
  const [hhPhone, setHhPhone] = useState("");
  const [spouseName, setSpouseName] = useState("");
  const [mission, setMission] = useState("");
  const [cnicImagePreview, setCnicImagePreview] = useState(null);
  const [result, setResult] = useState("");
  const [resultOtherSpecify, setResultOtherSpecify] = useState("");
  const [interviewStart, setInterviewStart] = useState(false);
  const [ucName, setUcname] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [ucs, setUcs] = useState([]);
  const [dehs, setDehs] = useState([]);
  const [villages, setVillages] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTehsil, setSelectedTehsil] = useState(null);
  const [selectedUc, setSelectedUc] = useState(null);
  const [selectedDeh, setSelectedDeh] = useState(null);
  const [selectedVillage, setSelectedVillage] = useState(null);

  const [dateValue, setDateValue] = useState(null);
  const [timeValue, setTimeValue] = useState(null);

  const [isDqaApproved, setIsDqaApproved] = useState(DqaApproved || false);
  const [isDqaRejected, setIsDqaRejected] = useState(false);
  const [errors, setErrors] = useState({});

  const [householdNumber, setHouseholdNumber] = useState("");
  const [householdAddress, setHouseholdAddress] = useState("");
  const [surveyType, setSurveyType] = useState(null);
  const [availableSurveyTypes, setAvailableSurveyTypes] = useState([]);

  const [spouseCnic, setSpouseCnic] = useState("");
  const [spouseCnicImagePreview, setSpouseCnicImagePreview] = useState(null);
  const [showSpouseCnicImageModal, setShowSpouseCnicImageModal] =
    useState(false);
  const isEmpty = (v) => !v || String(v).trim() === "";

  const isValidCNIC = (v) => {
    if (isEmpty(v)) return false;
    const cleanCnic = String(v).replace(/[-\s]/g, "");
    return /^[0-9]{13}$/.test(cleanCnic);
  };

  const isValidPhone = (v) => {
    if (isEmpty(v)) return false;
    const cleaned = String(v).replace(/[-\s]/g, "");
    return /^03[0-9]{9}$/.test(cleaned);
  };

  const isValidTextOnly = (v) => {
    if (isEmpty(v)) return false;
    return /^[a-zA-Z\s]+$/.test(v.trim());
  };

  const isValidNumberOnly = (v) => {
    if (isEmpty(v)) return false;
    return /^[0-9]+$/.test(v.trim());
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    const cleanPath = imagePath.replace(/^undefined\//, "");
    if (cleanPath.startsWith("http://") || cleanPath.startsWith("https://")) {
      return cleanPath;
    }
    const baseUrl = process.env.REACT_APP_BASE_URL || "";
    return `${baseUrl}/${cleanPath}`;
  };

  const getDisplayName = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath.split("/").pop();
    }
    return imagePath;
  };
  const missionOptions = useMemo(() => {
    if (!selectedDeh) return [];

    const selectedDehObj = dehs.find(
      (deh) => deh.deh_id.toString() === selectedDeh
    );

    if (!selectedDehObj) return [];

    const dehName = selectedDehObj.deh_name;

    return Array.from({ length: 25 }, (_, index) => {
      const missionNumber = String(index + 1).padStart(2, "0");
      const missionValue = `${dehName}_M${missionNumber}`;
      return {
        value: missionValue,
        label: missionValue,
      };
    });
  }, [selectedDeh, dehs]);

  const handleImageClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (cnicImagePreview) setShowImageModal(true);
  };

  const closeImageModal = (e) => {
    e?.stopPropagation();
    setShowImageModal(false);
  };

  const formatCNIC = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 5) {
      return cleaned;
    } else if (cleaned.length <= 12) {
      return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
    } else {
      return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 12)}-${cleaned.slice(
        12,
        13
      )}`;
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 11) {
      return cleaned;
    }
    return cleaned.slice(0, 11);
  };

  const handleCnicChange = (value, setter, fieldName) => {
    const cleanValue = value.replace(/\D/g, "");
    if (cleanValue.length <= 13) {
      const formatted = formatCNIC(cleanValue);
      setter(formatted);
      clearFieldError(fieldName);
    }
  };

  const handlePhoneChange = (value, setter, fieldName) => {
    const cleanValue = value.replace(/\D/g, "");
    if (cleanValue === "" || cleanValue.startsWith("03")) {
      if (cleanValue.length <= 11) {
        setter(cleanValue);
        clearFieldError(fieldName);
      }
    }
  };

  const handleTextOnlyChange = (value, setter, fieldName) => {
    const textValue = value.replace(/[^a-zA-Z\s]/g, "");
    setter(textValue);
    clearFieldError(fieldName);
  };

  const handleNumberOnlyChange = (value, setter, fieldName) => {
    const numberValue = value.replace(/\D/g, "");
    setter(numberValue);
    clearFieldError(fieldName);
  };
  const fetchSurveyStatus = async (householdNum, currentType, villageId) => {
    if (!villageId) return;
    try {
      const params = { household_number: householdNum, village_id: villageId };
      const response = await fetchData(
        "householdinformation-survey-status",
        "GET",
        null,
        {},
        params,
        navigate
      );
      if (response?.success) {
        const { next_types, next_type } = response.data;
        if (currentType) {
          // Record already has a type — show it as read-only
          setAvailableSurveyTypes([currentType]);
        } else if (next_types?.length || next_type) {
          setAvailableSurveyTypes(next_types?.length ? next_types : [next_type]);
        } else {
          setAvailableSurveyTypes([]);
        }
      }
    } catch (error) {
      console.error("Error fetching survey status:", error);
    }
  };

  const fetchLocationData = async () => {
    try {
      const districtRes = await fetchData("districts");
      setDistricts(districtRes?.data || []);
    } catch (error) {
      toast.error("Error loading location data");
    }
  };

  const validateId = (id) => {
    const isValid = Number.isInteger(parseInt(id, 10)) && id.length <= 6;
    return isValid;
  };

  const fetchHouseHold = async () => {
    if (!validateId(id)) {
      navigate('/404');
      return;
    }

    try {
      const response = await fetchData(
        `householdinformation/${id}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "/provinces"
      );

      const data = response?.data?.household_information;
      setIsDqaRejected(
        response?.data?.household_information?.verified_from_dqa === 2
      );

      if (data) {
        setHouseHoldInfo(data);
        setRespondentName(data.respondent_name || "");
        setRespondentLine(data.respondent_line_number || "");
        setHhName(data.hh_head_name || "");
        setHhCnic(data.hh_head_cnic_no || "");
        setHhPhone(data.hh_head_contact_no || "");
        setSpouseName(data.name_spouse || "");
        setSpouseCnic(data.spouse_cnic_no || "");
        setMission(data.mission || "");
        setResult(data.result_of_interview || "");
        setResultOtherSpecify(data.result_of_interview_other_specify || "");
        setInterviewStart(data.start_interview || false);
        setUcname(data.uc_name || "");
        const districtId =
          data.district_id?.toString() ||
          data.village?.district_id?.toString() ||
          null;
        const tehsilId =
          data.tehsil_id?.toString() ||
          data.village?.tehsil_id?.toString() ||
          null;
        const ucId = data.uc_id?.toString() || null;
        const dehId =
          data.deh_id?.toString() || data.village?.deh_id?.toString() || null;
        const villageId = data.village_id?.toString() || null;

        setSelectedDistrict(districtId);
        setSelectedTehsil(tehsilId);
        setSelectedUc(ucId);
        setSelectedDeh(dehId);
        setSelectedVillage(villageId);

        setDateValue(
          data.date_of_interview ? dayjs(data.date_of_interview) : null
        );

        if (data.time_of_interview) {
          let timeValue;
          try {
            if (data.time_of_interview.includes("T")) {
              timeValue = dayjs(data.time_of_interview);
            } else {
              timeValue = dayjs(
                `2000-01-01 ${data.time_of_interview}`,
                "YYYY-MM-DD HH:mm:ss"
              );
              if (!timeValue.isValid()) {
                timeValue = dayjs(
                  `2000-01-01 ${data.time_of_interview}`,
                  "YYYY-MM-DD HH:mm"
                );
              }
            }
            setTimeValue(timeValue.isValid() ? timeValue : null);
          } catch (error) {
            console.error("Error parsing time:", error);
            setTimeValue(null);
          }
        } else {
          setTimeValue(null);
        }

        setHouseholdNumber(data.household_number || "");
        setHouseholdAddress(data.household_address || "");
        setSurveyType(data.survey_type || null);

        // Fetch available survey types for this household_number
        if (data.household_number) {
          fetchSurveyStatus(data.household_number, data.survey_type, data.village_id);
        }

        if (data.cnic_image) {
          setCnicImagePreview(data.cnic_image);
        }

        if (data.spouse_cnic_image) {
          setSpouseCnicImagePreview(data.spouse_cnic_image);
        }
      }
    } catch (error) {
      console.error("Error fetching household data:", error);
      //toast.error("Failed to load household data.");
      navigate('/404');
    }
  };

  useEffect(() => {
    fetchHouseHold();
    fetchLocationData();
  }, []);
  useEffect(() => {
    if (selectedDistrict) {
      fetchData("tehsils", "GET", null, "", {
        district_id: selectedDistrict,
      })
        .then((data) => {
          setTehsils(data?.data || []);
        })
        .catch((error) => {
          console.error("Error fetching tehsils:", error);
          setTehsils([]);
        });
    } else {
      setTehsils([]);
    }
  }, [selectedDistrict]);

    useEffect(() => {
    setIsDqaApproved(DqaApproved || false);
  }, [DqaApproved]);

  useEffect(() => {
    if (selectedTehsil) {
      fetchData("dehs", "GET", null, "", {
        tehsil_id: selectedTehsil,
      })
        .then((data) => {
          setDehs(data?.data || []);
        })
        .catch((error) => {
          console.error("Error fetching Dehs:", error);
          setDehs([]);
        });
    } else {
      setDehs([]);
    }
  }, [selectedDeh]);
  useEffect(() => {
    if (selectedDeh) {
      fetchData("villages", "GET", null, "", {
        deh_id: selectedDeh,
      })
        .then((data) => {
          setVillages(data?.data || []);

        })
        .catch((error) => {
          console.error("Error fetching Villages:", error);
          setVillages([]);
        });
    } else {
      setVillages([]);
    }
  }, [selectedDeh]);

  const handleDistrictChange = (districtId) => {
    setSelectedDistrict(districtId);
    setSelectedTehsil("");
    setSelectedDeh("");
    setSelectedVillage("");
    setMission("");

    setErrors((prev) => ({
      ...prev,
      selectedDistrict: undefined,
      selectedTehsil: undefined,
      selectedUc: undefined,
      selectedDeh: undefined,
      selectedVillage: undefined,
      mission: undefined,
    }));
  };


  const handleTehsilChange = (tehsilId) => {
    setSelectedTehsil(tehsilId);
    setSelectedDeh("");
    setSelectedVillage("");
    setMission("");

    setErrors((prev) => ({
      ...prev,
      selectedTehsil: undefined,
      selectedDeh: undefined,
      selectedVillage: undefined,
      mission: undefined,
    }));
  };


  const handleDehChange = (dehId) => {
    setSelectedDeh(dehId);
    setSelectedVillage("");
    setMission("");

    setErrors((prev) => ({
      ...prev,
      selectedDeh: undefined,
      selectedVillage: undefined,
      mission: undefined,
    }));
  };
  const clearFieldError = (fieldName) => {
    setErrors((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));
  };
  const districtOptions = districts.map((district) => ({
    value: String(district.district_id),
    label: district.district_name,
  }));
  const tehsilOptions = tehsils
    .filter(
      (t) => !selectedDistrict || t.district_id.toString() === selectedDistrict
    )
    .map((t) => ({
      value: String(t.tehsil_id),
      label: t.tehsil_name,
    }));

  const dehOptions = dehs
    .filter((d) => !selectedTehsil || d.tehsil_id.toString() === selectedTehsil)
    .map((d) => ({
      value: String(d.deh_id),
      label: d.deh_name,
    }));
  const villageOptions = villages
    .filter((v) => !selectedDeh || v.deh_id.toString() === selectedDeh)
    .map((v) => ({
      value: v.village_id,
      label: v.village_name,
    }));
  const handleSpouseCnicImageClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (spouseCnicImagePreview) setShowSpouseCnicImageModal(true);
  };

  const closeSpouseCnicImageModal = (e) => {
    e?.stopPropagation();
    setShowSpouseCnicImageModal(false);
  };

  const resultOptions = [
    { label: "Ongoing/ Continue", value: "Ongoing/ Continue" },
    { label: "Completed", value: "Completed" },
    { label: "Refused", value: "Refused" },
    { label: "Additional Room", value: "Additional Room" },
    { label: "Partially Completed", value: "Partially Completed" },
    {
      label: "Respondent incapacitated/ No HH member at Home",
      value: "Respondent incapacitated/ No HH member at Home",
    },
    {
      label: "Entire HH absent for extended period of time",
      value: "Entire HH absent for extended period of time",
    },
    {
      label: "Dwelling destroyed/ not found",
      value: "Dwelling destroyed/ not found",
    },
    { label: "Others", value: "Others" },
  ];

  const handleUpdate = async () => {
    const newErrors = {};
    if (isEmpty(selectedDistrict))
      newErrors.selectedDistrict = "District is required.";
    if (isEmpty(selectedTehsil))
      newErrors.selectedTehsil = "Tehsil is required.";
    if (isEmpty(selectedDeh)) newErrors.selectedDeh = "Deh is required.";
    if (isEmpty(mission)) {
      newErrors.mission = "Mission is required.";
    }
    if (isEmpty(selectedVillage))
      newErrors.selectedVillage = "Village is required.";

    if (interviewStart) {
      if (isEmpty(householdNumber))
        newErrors.householdNumber = "Household number is required.";

      if (isEmpty(result)) newErrors.result = "Interview result is required.";

      if (result === "Others" && isEmpty(resultOtherSpecify)) {
        newErrors.resultOtherSpecify = "Please specify the other reason.";
      }

      if (!dateValue) newErrors.dateValue = "Date of interview is required.";
      if (!timeValue) newErrors.timeValue = "Time of interview is required.";

      if (isEmpty(respondentName)) {
        newErrors.respondentName = "Respondent name is required.";
      } else if (!isValidTextOnly(respondentName)) {
        newErrors.respondentName =
          "Respondent name should contain only letters and spaces.";
      }

      if (isEmpty(respondentLine)) {
        newErrors.respondentLine = "Respondent line number is required.";
      } else if (!isValidPhone(respondentLine)) {
        newErrors.respondentLine =
          "Enter a valid phone number (03XXXXXXXXX - 11 digits starting with 03).";
      }

      if (isEmpty(hhName)) {
        newErrors.hhName = "HH Head name is required.";
      } else if (!isValidTextOnly(hhName)) {
        newErrors.hhName =
          "HH Head name should contain only letters and spaces.";
      }
      if (result === "Ongoing/ Continue" || result === "Completed") {
        if (isEmpty(hhCnic)) {
          newErrors.hhCnic = "HH Head CNIC number is required.";
        } else if (!isValidCNIC(hhCnic)) {
          newErrors.hhCnic =
            "Enter a valid CNIC (13 digits in format: 45102-0000000-7).";
        }

        if (isEmpty(hhPhone)) {
          newErrors.hhPhone = "HH Head contact number is required.";
        } else if (!isValidPhone(hhPhone)) {
          newErrors.hhPhone =
            "Enter a valid phone number (03XXXXXXXXX - 11 digits starting with 03).";
        }

        if (isEmpty(spouseName)) {
          newErrors.spouseName = "Spouse name is required.";
        } else if (!isValidTextOnly(spouseName)) {
          newErrors.spouseName =
            "Spouse name should contain only letters and spaces.";
        }

        if (isEmpty(spouseCnic)) {
          newErrors.spouseCnic = "Spouse CNIC is required.";
        } else if (!isValidCNIC(spouseCnic)) {
          newErrors.spouseCnic =
            "Enter a valid CNIC (13 digits in format: 45102-0000000-7).";
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // toast.error("Please fill in all required fields before updating.");
      return;
    }

    setErrors({});

    if (!id) {
      toast.error("Household ID is missing.");
      return;
    }

    const payload = {
      uc_name: ucName || "",
      // uc_id: selectedUc || "",
      village_id: selectedVillage || "",
      district_id: selectedDistrict || "",
      tehsil_id: selectedTehsil || "",
      deh_id: selectedDeh || "",
      household_number: householdNumber,
      household_address: householdAddress,
      start_interview: interviewStart,
      survey_type: surveyType || undefined,
    };

    if (interviewStart) {
      payload.date_of_interview = dateValue?.format("YYYY-MM-DD") || "";
      payload.time_of_interview = timeValue?.format("HH:mm:ss") || "";
      payload.respondent_name = respondentName || "";
      payload.respondent_line_number = respondentLine || "";
      payload.hh_head_name = hhName || "";
      payload.uc_name = ucName || "";
      payload.hh_head_cnic_no = hhCnic || "";
      payload.hh_head_contact_no = hhPhone || "";
      payload.name_spouse = spouseName || "";
      payload.spouse_cnic_no = spouseCnic || "";
      payload.mission = mission || "";
      payload.result_of_interview = result || "";
      payload.result_of_interview_other_specify =
        result === "Others" ? resultOtherSpecify : "";
    }

    try {
      const response = await fetchData(
        `householdinformation-update/${id}`,
        "PUT",
        payload,
        {},
        {},
        navigate,
        "/provinces"
      );

      if (response?.success) {
        toast.success(response?.message || "Update successful");
        await fetchHouseHold();
      } else {
        toast.error(response?.message || "Update failed");

        if (response?.message?.includes("already registered")) {
          setErrors((prev) => ({
            ...prev,
            hhCnic: response.message,
          }));
        }
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Error while updating household information.";
      toast.error(errorMsg);
      console.error(err);
    }
  };

  if (!houseHoldInfo) return <p>Loading...</p>;

  return (
    <div className="bg-white rounded">
      <div className="flex justify-between items-center">
        {isDqaApproved && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Information
            </h2>
            <span className="bg-blue-100 text-blue-800 px-1 py-1 rounded-full text-sm font-semibold">
              Approved from DQA
            </span>
          </>
        )}
        {isDqaRejected && (
          <>
            <h2 className="text-teal-700 text-xl font-semibold">
              Household Information
            </h2>

            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
              Rejected from DQA
            </span>
          </>
        )}
      </div>
      <div className="flex justify-between items-center mb-6"></div>

      <form className="w-full">
        {/* Survey Type field */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            {houseHoldInfo?.survey_type ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Survey Type {!isDqaApproved && !isDqaRejected && <span className="text-red-500">*</span>}
                </label>
                <div className="flex items-center h-[42px] border border-gray-300 rounded-md px-3 bg-gray-50">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      houseHoldInfo.survey_type === "Baseline"
                        ? "bg-blue-100 text-blue-800"
                        : houseHoldInfo.survey_type === "Midline"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-purple-100 text-purple-800"
                    }`}
                  >
                    {houseHoldInfo.survey_type}
                  </span>
                </div>
              </div>
            ) : (
              <div>
                <ThinDropdownWithSearch
                  label="Survey Type"
                  id="surveyType"
                  options={availableSurveyTypes.map((t) => ({ value: t, label: t }))}
                  value={surveyType}
                  onChange={(val) => {
                    setSurveyType(val);
                    clearFieldError("surveyType");
                  }}
                  required
                  disabled={isDqaApproved || isDqaRejected || availableSurveyTypes.length === 0}
                  style={{ width: "100%" }}
                />
                {availableSurveyTypes.length === 0 && (
                  <p className="text-[0.75rem] text-orange-500 mt-1">
                    No further survey type can be added for this household (Endline already exists).
                  </p>
                )}
                {errors.surveyType && (
                  <p className="text-[0.8rem] text-red-600 mt-1">{errors.surveyType}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            <ThinTextField
              label="Enumerator's Name"
              value={houseHoldInfo.users?.name || ""}
              disabled
              style={{ width: "100%" }}
            />
          </div>
          <div className="w-full">
            <ThinTextField
              label="Enumerator's Code"
              value={houseHoldInfo.users?.user_code || ""}
              disabled
              style={{ width: "100%" }}
            />
          </div>
          <div className="w-full">
            <ThinTextField
              label="Supervisor Name & Code"
              value={
                houseHoldInfo.users?.supervisor
                  ? `${houseHoldInfo.users.supervisor.name} - ${
                      houseHoldInfo.users.supervisor.user_code || ""
                    }`
                  : ""
              }
              disabled
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            <ThinTextField
              label="Latitude"
              value={houseHoldInfo?.latitude || ""}
              disabled
              placeholder="Latitude"
              style={{ width: "100%" }}
            />
          </div>
          <div className="w-full">
            <ThinTextField
              label="Longitude"
              value={houseHoldInfo?.longitude || ""}
              disabled
              placeholder="Longitude"
              style={{ width: "100%" }}
            />
          </div>
          <div className="w-full">
            <ThinDropdownWithSearch
              label="District"
              id="district"
              options={districtOptions}
              value={selectedDistrict}
              onChange={(districtId) => {
                handleDistrictChange(districtId);
                clearFieldError("selectedDistrict");
              }}
              required
              disabled={isDqaApproved || isDqaRejected}
              style={{ width: "100%" }}
            />
            {errors.selectedDistrict && (
              <p className="text-[0.8rem] text-red-600 mt-1">
                {errors.selectedDistrict}
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            <ThinDropdownWithSearch
              label="Tehsil"
              id="tehsil"
              options={tehsilOptions}
              value={selectedTehsil}
              onChange={(tehsilId) => {
                handleTehsilChange(tehsilId);
                clearFieldError("selectedTehsil");
              }}
              required
              disabled={isDqaApproved || isDqaRejected || !selectedDistrict}
              style={{ width: "100%" }}
            />
            {errors.selectedTehsil && (
              <p className="text-[0.8rem] text-red-600 mt-1">
                {errors.selectedTehsil}
              </p>
            )}
          </div>

          {/* <div className="w-full">
            <ThinDropdownWithSearch
              label="UC"
              id="uc"
              options={ucOptions}
              value={selectedUc}
              onChange={(ucId) => {
                handleUcChange(ucId);
                clearFieldError("selectedUc");
              }}
              required
              disabled={isDqaApproved || isDqaRejected || !selectedTehsil}
              style={{ width: "100%" }}
            />
            {errors.selectedUc && (
              <p className="text-[0.8rem] text-red-600 mt-1">
                {errors.selectedUc}
              </p>
            )}
          </div> */}
          {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4"> */}
          <div className="w-full">
            <ThinTextField
              label="UC"
              value={ucName}
              onChange={(e) => {
                handleTextOnlyChange(e.target.value, setUcname, "ucName");
              }}
              placeholder="Enter UC"
              isRequired={false}
              style={{ width: "100%" }}
              disabled={isDqaApproved || isDqaRejected}
            />
          </div>

          <div className="w-full">
            <ThinDropdownWithSearch
              label="Deh"
              id="deh"
              options={dehOptions}
              value={selectedDeh}
              // onChange={(dehId) => {
              onChange={(dehId) => {
                handleDehChange(dehId);
                clearFieldError("selectedDeh");
              }}
              required
              // disabled={isDqaApproved || isDqaRejected || !selectedUc}
              disabled={isDqaApproved || isDqaRejected || !selectedTehsil}
              style={{ width: "100%" }}
            />
            {errors.selectedDeh && (
              <p className="text-[0.8rem] text-red-600 mt-1">
                {errors.selectedDeh}
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="w-full">
            <ThinDropdownWithSearch
              label="Mission"
              id="mission"
              options={missionOptions}
              value={mission}
              onChange={(value) => {
                setMission(value);
                clearFieldError("mission");
              }}
              required
              disabled={isDqaApproved || isDqaRejected || !selectedDeh}
              style={{ width: "100%" }}
            />
            {errors.mission && (
              <p className="text-[0.8rem] text-red-600 mt-1">
                {errors.mission}
              </p>
            )}
          </div>

          <div className="w-full">
            <ThinDropdownWithSearch
              label="Village"
              id="village"
              options={villageOptions}
              value={selectedVillage}
              // onChange={(villageId) => {
              //   setSelectedVillage(villageId);
              //   clearFieldError("selectedVillage");
              // }}
              onChange={(value) => {
                setSelectedVillage(value);
                setMission("");
              }}
              required
              disabled={isDqaApproved || isDqaRejected || !selectedDeh}
              style={{ width: "100%" }}
            />
            {errors.selectedVillage && (
              <p className="text-[0.8rem] text-red-600 mt-1">
                {errors.selectedVillage}
              </p>
            )}
          </div>

          <div className="w-full">
            <ThinTextField
              label="Household Address"
              value={householdAddress}
              onChange={(e) => {
                setHouseholdAddress(e.target.value);
              }}
              disabled={isDqaApproved || isDqaRejected}
              isRequired={false}
              placeholder="Enter household address"
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <div className="mb-6">
          <label className="block font-medium text-gray-700 mb-2">
            Do you want to start the interview?
          </label>
          <div className="flex items-center gap-6">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="interviewStart"
                value="yes"
                checked={interviewStart === true}
                readOnly
                disabled
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="interviewStart"
                value="no"
                checked={interviewStart === false}
                readOnly
                disabled
              />
              <span className="ml-2">No</span>
            </label>
          </div>
        </div>

        {interviewStart && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="w-full">
                <ThinTextField
                  label="Date of Interview"
                  type="date"
                  value={dateValue ? dateValue.format("YYYY-MM-DD") : ""}
                  onChange={(e) => {
                    if (e.target.value) {
                      setDateValue(dayjs(e.target.value));
                    } else {
                      setDateValue(null);
                    }
                    clearFieldError("dateValue");
                  }}
                  readOnly={true}
                  disabled={isDqaApproved || isDqaRejected}
                  style={{ width: "100%" }}
                />
                {errors.dateValue && (
                  <p className="text-[0.8rem] text-red-600 mt-1">
                    {errors.dateValue}
                  </p>
                )}
              </div>

              <div className="w-full">
                <ThinTextField
                  label="Interview Time"
                  type="time"
                  value={timeValue ? timeValue.format("HH:mm") : ""}
                  onChange={(e) => {
                    if (e.target.value) {
                      const [hours, minutes] = e.target.value.split(":");
                      setTimeValue(
                        dayjs().hour(parseInt(hours)).minute(parseInt(minutes)),
                      );
                    } else {
                      setTimeValue(null);
                    }
                    clearFieldError("timeValue");
                  }}
                  readOnly={true}
                  disabled={isDqaApproved || isDqaRejected}
                  style={{ width: "100%" }}
                />
                {errors.timeValue && (
                  <p className="text-[0.8rem] text-red-600 mt-1">
                    {errors.timeValue}
                  </p>
                )}
              </div>

              <div className="w-full">
                <ThinTextField
                  label="Respondent Name"
                  value={respondentName}
                  onChange={(e) => {
                    handleTextOnlyChange(
                      e.target.value,
                      setRespondentName,
                      "respondentName",
                    );
                  }}
                  disabled={isDqaApproved || isDqaRejected}
                  placeholder="Enter respondent name"
                  style={{ width: "100%" }}
                />
                {errors.respondentName && (
                  <p className="text-[0.8rem] text-red-600 mt-1">
                    {errors.respondentName}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="w-full">
                <ThinTextField
                  label="Contact number Respondent"
                  value={respondentLine}
                  onChange={(e) => {
                    handlePhoneChange(
                      e.target.value,
                      setRespondentLine,
                      "respondentLine",
                    );
                  }}
                  disabled={isDqaApproved || isDqaRejected}
                  placeholder="03XXXXXXXXX (11 digits)"
                  style={{ width: "100%" }}
                  maxLength={11}
                />
                {errors.respondentLine && (
                  <p className="text-[0.8rem] text-red-600 mt-1">
                    {errors.respondentLine}
                  </p>
                )}
              </div>

              <div className="w-full">
                <ThinTextField
                  label=" HH Head Name"
                  value={hhName}
                  onChange={(e) => {
                    handleTextOnlyChange(e.target.value, setHhName, "hhName");
                  }}
                  disabled={isDqaApproved || isDqaRejected}
                  placeholder="Enter HH head name"
                  style={{ width: "100%" }}
                />
                {errors.hhName && (
                  <p className="text-[0.8rem] text-red-600 mt-1">
                    {errors.hhName}
                  </p>
                )}
              </div>

              <div className="w-full">
                <ThinTextField
                  label="Household Number"
                  value={householdNumber}
                  onChange={(e) => {
                    handleNumberOnlyChange(
                      e.target.value,
                      setHouseholdNumber,
                      "householdNumber",
                    );
                  }}
                  disabled={isDqaApproved || isDqaRejected}
                  readOnly
                  placeholder="Enter household number"
                  style={{ width: "100%" }}
                />
                {errors.householdNumber && (
                  <p className="text-[0.8rem] text-red-600 mt-1">
                    {errors.householdNumber}
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="w-full">
                <ThinDropdownWithSearch
                  label="Result of Interview"
                  id="interviewResult"
                  options={resultOptions}
                  value={result}
                  onChange={(value) => {
                    setResult(value);
                    clearFieldError("result");

                    if (value !== "Others") {
                      setResultOtherSpecify("");
                    }
                  }}
                  required
                  disabled={true}
                  style={{ width: "100%" }}
                />
                {errors.result && (
                  <p className="text-[0.8rem] text-red-600 mt-1">
                    {errors.result}
                  </p>
                )}
              </div>

              {result === "Others" && (
                <div className="w-full">
                  <ThinTextField
                    label="Please Specify Other Reason"
                    value={resultOtherSpecify}
                    onChange={(e) => {
                      setResultOtherSpecify(e.target.value);
                      clearFieldError("resultOtherSpecify");
                    }}
                    disabled={isDqaApproved || isDqaRejected}
                    readOnly={true}
                    placeholder="Enter the specific reason..."
                    style={{ width: "100%" }}
                  />
                  {errors.resultOtherSpecify && (
                    <p className="text-[0.8rem] text-red-600 mt-1">
                      {errors.resultOtherSpecify}
                    </p>
                  )}
                </div>
              )}

              {(result === "Ongoing/ Continue" || result === "Completed") && (
                <>
                  <div className="w-full">
                    <ThinTextField
                      label="HH Head CNIC No"
                      value={hhCnic}
                      onChange={(e) => {
                        handleCnicChange(e.target.value, setHhCnic, "hhCnic");
                      }}
                      disabled={isDqaApproved || isDqaRejected}
                      placeholder="45102-0000000-7"
                      style={{ width: "100%" }}
                      maxLength={15}
                    />
                    {errors.hhCnic && (
                      <p className="text-[0.8rem] text-red-600 mt-1">
                        {errors.hhCnic}
                      </p>
                    )}
                  </div>

                  <div className="w-full">
                    {cnicImagePreview && (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          CNIC Picture of Head (Front Side only)
                        </label>
                        <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                          <button
                            type="button"
                            className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0"
                            onClick={handleImageClick}
                            title="Click to view image"
                          >
                            <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                            <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">
                              CNIC Picture
                            </span>
                            <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            {(result === "Ongoing/ Continue" || result === "Completed") && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="w-full">
                    <ThinTextField
                      label="HH Head Contact No"
                      value={hhPhone}
                      onChange={(e) => {
                        handlePhoneChange(
                          e.target.value,
                          setHhPhone,
                          "hhPhone",
                        );
                      }}
                      disabled={isDqaApproved || isDqaRejected}
                      placeholder="03XXXXXXXXX (11 digits)"
                      style={{ width: "100%" }}
                      maxLength={11}
                    />
                    {errors.hhPhone && (
                      <p className="text-[0.8rem] text-red-600 mt-1">
                        {errors.hhPhone}
                      </p>
                    )}
                  </div>
                  <div className="w-full">
                    <ThinTextField
                      label="Name of Spouse (Husband / Wife)"
                      value={spouseName}
                      onChange={(e) => {
                        handleTextOnlyChange(
                          e.target.value,
                          setSpouseName,
                          "spouseName",
                        );
                      }}
                      disabled={isDqaApproved || isDqaRejected}
                      placeholder="Enter spouse name"
                      style={{ width: "100%" }}
                    />
                    {errors.spouseName && (
                      <p className="text-[0.8rem] text-red-600 mt-1">
                        {errors.spouseName}
                      </p>
                    )}
                  </div>

                  <div className="w-full">
                    <ThinTextField
                      label="Spouse CNIC No"
                      value={spouseCnic}
                      onChange={(e) => {
                        handleCnicChange(
                          e.target.value,
                          setSpouseCnic,
                          "spouseCnic",
                        );
                      }}
                      disabled={isDqaApproved || isDqaRejected || !!houseHoldInfo?.spouse_cnic_no}
                      placeholder="45102-0000000-7"
                      style={{ width: "100%" }}
                      maxLength={15}
                    />
                    {errors.spouseCnic && (
                      <p className="text-[0.8rem] text-red-600 mt-1">
                        {errors.spouseCnic}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="w-full">
                    {spouseCnicImagePreview ? (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          CNIC Picture of Spouse - Optional
                        </label>
                        <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                          <button
                            type="button"
                            className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 border border-transparent hover:border-blue-200 w-full text-left min-h-0"
                            onClick={handleSpouseCnicImageClick}
                            title="Click to view spouse CNIC image"
                          >
                            <i className="fa fa-image text-blue-500 text-sm flex-shrink-0"></i>
                            <span className="text-xs text-blue-600 hover:text-blue-800 font-medium truncate">
                              Spouse CNIC Picture
                            </span>
                            <i className="fa fa-eye text-xs text-blue-400 flex-shrink-0 ml-auto"></i>
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Spouse CNIC Picture - Optional
                        </label>
                        <div className="border border-gray-300 rounded-md p-2 bg-gray-50 h-[42px] flex items-center">
                          <span className="text-xs text-gray-500">
                            No image uploaded
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {!isDqaApproved && !isDqaRejected && (
          <div className="flex justify-end mt-8">
            <button
              type="button"
              onClick={handleUpdate}
              className="bg-teal-600 text-white rounded-lg px-4 py-2 hover:bg-teal-700 transition-colors"
            >
              Update
            </button>
          </div>
        )}
      </form>
      {showImageModal && cnicImagePreview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]"
          onClick={closeImageModal}
        >
          <div
            className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeImageModal}
              className="absolute top-2 right-2 text-gray-600 hover:text-black"
            >
              ✕
            </button>

            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">
              CNIC Picture of Head (Front Side only)
            </h2>

            <img
              src={getImageUrl(cnicImagePreview)}
              alt="CNIC Picture of Head"
              className="max-h-[80vh] object-contain rounded-md border border-gray-300"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/600x400?text=Image+not+found";
              }}
            />
          </div>
        </div>
      )}

      {showSpouseCnicImageModal && spouseCnicImagePreview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]"
          onClick={closeSpouseCnicImageModal}
        >
          <div
            className="relative bg-white rounded-lg shadow-lg p-4 max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeSpouseCnicImageModal}
              className="absolute top-2 right-2 text-gray-600 hover:text-black"
            >
              ✕
            </button>

            <h2 className="text-center text-gray-800 mb-3 text-lg font-semibold">
              CNIC Picture of Spouse
            </h2>

            <img
              src={getImageUrl(spouseCnicImagePreview)}
              alt="CNIC Picture of Spouse"
              className="max-h-[80vh] object-contain rounded-md border border-gray-300"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/600x400?text=Image+not+found";
              }}
            />
            <a
              href={getImageUrl(spouseCnicImagePreview)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 text-sm underline mt-2 text-center"
            >
              View Full Image
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default InformationPanel;
